import type { ReactNode } from "react";
import { AlertCircle, Inbox } from "lucide-react";

export function PageHeader({ title, description, actions }: { title: string; description: string; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div><h1>{title}</h1><p>{description}</p></div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  );
}

export function StatusBadge({ value, label }: { value: string; label?: string }) {
  const tone = ["enabled", "closed", "succeeded", "active"].includes(value)
    ? "positive"
    : ["open", "failed", "disabled", "revoked"].includes(value)
      ? "negative"
      : "warning";
  return <span className={`status-badge ${tone}`}><span />{label || statusLabel(value)}</span>;
}

export function DataState({ loading, error, empty, emptyTitle = "暂无匹配数据", emptyDescription = "调整筛选条件后再试", children }: {
  loading: boolean;
  error?: Error | null;
  empty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  children: ReactNode;
}) {
  if (loading) return <div className="data-state loading-state" aria-label="正在读取数据"><div className="skeleton-stack"><span /><span /><span /></div></div>;
  if (error) return <div className="data-state error"><AlertCircle size={20} /><div><strong>数据读取失败</strong><span>{error.message}</span></div></div>;
  if (empty) return <div className="data-state empty-state"><Inbox size={20} /><div><strong>{emptyTitle}</strong><span>{emptyDescription}</span></div></div>;
  return <>{children}</>;
}

export function AuditLine({ createdBy, createdAt, updatedBy, updatedAt }: {
  createdBy: string;
  createdAt: number;
  updatedBy: string;
  updatedAt: number;
}) {
  return (
    <div className="audit-line">
      <span>创建：{actorLabel(createdBy)} · {formatTime(createdAt)}</span>
      <span>修改：{actorLabel(updatedBy)} · {formatTime(updatedAt)}</span>
    </div>
  );
}

export function formatTime(value: number) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).format(new Date(value * 1000));
}

export function formatMoney(value: string | number, currency = "CNY") {
  const parsed = Number(value || 0);
  const amount = Number.isFinite(parsed) ? parsed : 0;
  const unit = currency.trim().toUpperCase() || "CNY";
  try {
    return new Intl.NumberFormat("zh-CN", { style: "currency", currency: unit, minimumFractionDigits: 2 }).format(amount);
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    const formatted = new Intl.NumberFormat("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 8 }).format(amount);
    return `${formatted} ${unit}`;
  }
}

export function formatQuantity(value: string | number) {
  const raw = String(value).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return raw;
  const [integer, fraction = ""] = raw.split(".");
  const significantFraction = fraction.replace(/0+$/, "");
  if (significantFraction) return `${integer}.${significantFraction}`;
  return integer === "-0" ? "0" : integer;
}

function actorLabel(actor: string) {
  if (actor === "system") return "系统";
  if (actor.startsWith("super_admin:")) return actor.slice("super_admin:".length);
  if (actor.startsWith("admin:")) return `管理员 #${actor.slice("admin:".length)}`;
  return actor || "-";
}

function statusLabel(value: string) {
  return ({
    enabled: "已启用", disabled: "已停用", closed: "健康", open: "熔断中",
    half_open: "探测中", active: "有效", revoked: "已撤销", succeeded: "成功",
    failed: "失败", queued: "排队中", running: "执行中", uncertain: "待确认",
  } as Record<string, string>)[value] || value;
}
